import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api.js';
import { Button, Modal, Spinner, cx, IconExternal, IconWarning } from '../../components/ui/index.js';

/** ความกว้างจอที่ใช้ทดสอบ — อิงอุปกรณ์จริงที่พบบ่อย ไม่ใช่ตัวเลขกลม ๆ */
const DEVICES = [
  { key: 'mobile', label: 'มือถือ', width: 390, hint: 'iPhone 14/15' },
  { key: 'tablet', label: 'แท็บเล็ต', width: 834, hint: 'iPad' },
  { key: 'desktop', label: 'เดสก์ท็อป', width: 0, hint: 'เต็มความกว้าง' },
] as const;

type DeviceKey = (typeof DEVICES)[number]['key'];

interface PreviewLink {
  url: string;
  expiresIn: number;
  status: string;
}

export function PreviewModal({
  open, onClose, postId, liveUrl, onBeforeOpen,
}: {
  open: boolean;
  onClose: () => void;
  postId: string;
  /** URL หน้าจริง — มีเฉพาะบทความที่เผยแพร่แล้ว */
  liveUrl: string | null;
  /** บันทึกก่อนขอลิงก์ตัวอย่าง ไม่งั้นจะเห็นเนื้อหาเวอร์ชันก่อนหน้า */
  onBeforeOpen: () => Promise<void>;
}) {
  const [device, setDevice] = useState<DeviceKey>('desktop');
  const [link, setLink] = useState<PreviewLink | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setLink(null); setError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        await onBeforeOpen();
        const r = await api.post<{ data: PreviewLink }>(`/v1/posts/${postId}/preview-link`, {});
        if (!cancelled) setLink(r.data);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
    // ตั้งใจดูแค่ open/postId — onBeforeOpen เปลี่ยนตัวตนทุก render ของ component แม่
    // ถ้าใส่เข้าไปด้วยจะยิงขอลิงก์ใหม่ไม่รู้จบ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId]);

  const width = DEVICES.find((d) => d.key === device)?.width ?? 0;

  return (
    <Modal open={open} onClose={onClose} title="ตัวอย่างก่อนเผยแพร่" size="xl">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 py-2.5">
        <div className="flex items-center gap-0.5 rounded-lg bg-[var(--sunken)] p-0.5">
          {DEVICES.map((d) => (
            <button
              key={d.key}
              type="button"
              title={d.hint}
              aria-pressed={device === d.key}
              onClick={() => setDevice(d.key)}
              className={cx(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                device === d.key
                  ? 'bg-[var(--surface)] text-[var(--ink)] shadow-sm'
                  : 'text-[var(--ink-2)] hover:text-[var(--ink)]',
              )}
            >
              {d.label}
            </button>
          ))}
        </div>
        {width > 0 && <span className="text-xs tabular-nums text-[var(--ink-3)]">{width}px</span>}

        <div className="ml-auto flex items-center gap-2">
          {link && (
            <a href={link.url} target="_blank" rel="noopener">
              <Button size="sm" variant="ghost" icon={<IconExternal size={13} />}>เปิดในแท็บใหม่</Button>
            </a>
          )}
          {liveUrl && (
            <a href={liveUrl} target="_blank" rel="noopener">
              <Button size="sm" variant="outline" icon={<IconExternal size={13} />}>ดูหน้าจริง</Button>
            </a>
          )}
        </div>
      </div>

      <div className="min-h-[62vh] bg-[var(--sunken)] p-3">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-sm text-[var(--bad)]">
            <IconWarning size={15} /> {error}
          </div>
        )}
        {!error && !link && (
          <div className="grid h-[62vh] place-items-center text-sm text-[var(--ink-3)]">
            <span className="flex items-center gap-2"><Spinner className="h-4 w-4" /> กำลังบันทึกและเตรียมตัวอย่าง…</span>
          </div>
        )}
        {link && (
          <iframe
            // key ผูกกับความกว้าง เพื่อให้ iframe โหลดใหม่ทุกครั้งที่สลับอุปกรณ์
            // สคริปต์ในหน้าที่อ่านความกว้างตอนโหลดจึงได้ค่าที่ถูกต้อง
            key={`${link.url}-${width}`}
            src={link.url}
            title="ตัวอย่างบทความ"
            className="mx-auto block h-[62vh] rounded-lg border border-[var(--line)] bg-white shadow-sm"
            style={{ width: width > 0 ? `${width}px` : '100%' }}
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        )}
      </div>
    </Modal>
  );
}
